import { useState, useCallback } from 'react'
import { average } from 'lib/array/arrayUtils'
import { getMetresDistanceFromLatLong } from 'lib/flights/getDistanceFromLatLong'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'

type OfferTypes = App.HotelOffer | App.BedbankOffer | App.HotelOfferSummary | App.BedbankOfferSummary | App.OfferListMetaData
type FullOfferTypes = App.HotelOffer | App.BedbankOffer
type OfferWithLocation = FullOfferTypes | App.OfferListMetaData

interface Geolocation {
  longitude: number
  latitude: number
}

function hasLongitudeAndLatitude(location: { longitude?: number, latitude?: number }): boolean {
  return location.longitude !== undefined && location.latitude !== undefined
}

function getGeolocation(offer: OfferWithLocation): Geolocation
function getGeolocation(offer: OfferTypes): Geolocation | undefined
function getGeolocation(offer: OfferTypes): Geolocation | undefined {
  return isOfferListMeta(offer) ? offer.location : offer.property
}

export function isLoadedOffer(offer: OfferTypes): offer is FullOfferTypes {
  return 'id' in offer
}

export function isOfferListMeta(offer: App.AnyOffer | App.OfferListMetaData): offer is App.OfferListMetaData {
  return 'offerId' in offer
}

function hasValidLocation(offer: OfferTypes): offer is OfferWithLocation {
  const propertyOrTour = getGeolocation(offer)
  return !!propertyOrTour && hasLongitudeAndLatitude(propertyOrTour)
}

export function getMarkersByOffers(offers: Array<OfferTypes>, includeOneOfferPerMarker?: boolean): Array<App.SearchMapMarker> {
  const offerGroups = groupOffersAtSameCoordinates(offers.filter(hasValidLocation))
  if (includeOneOfferPerMarker) {
    return offerGroups.map(offers => getMarkerByOfferGroup([offers[0]]))
  }
  return offerGroups.map(offers => getMarkerByOfferGroup(offers))
}

function getMarkerByOfferGroup(offers: Array<OfferWithLocation>): App.SearchMapMarker {
  const latitude = average(offers, offer => getGeolocation(offer).latitude)
  const longitude = average(offers, offer => getGeolocation(offer).longitude)

  return {
    id: offers.map(offer => getOfferId(offer)).join(','),
    latitude: parseFloat((latitude).toFixed(6)),
    longitude: parseFloat((longitude).toFixed(6)),
    offers,
  }
}

export function getOfferId(offer: App.AnyOffer | App.OfferListMetaData): string {
  if (isOfferListMeta(offer)) {
    return offer.offerId
  } else {
    return offer.id
  }
}

export function getDisplayMarkerIdsMatchingOfferIds(
  fullMarkers: Array<App.SearchMapMarker>,
  displayMarkers: Array<App.SearchMapMarker>,
  offerIds: ReadonlySet<string | false | undefined>,
): Set<string> {
  const markerIds = new Set<string>()

  for (let index = 0; index < fullMarkers.length; index++) {
    const displayMarkerId = displayMarkers[index]?.id

    if (!displayMarkerId) {
      continue
    }

    if (fullMarkers[index].offers.some((offer) => offerIds.has(getOfferId(offer)))) {
      markerIds.add(displayMarkerId)
    }
  }

  return markerIds
}

export function getBoundsDisambiguationCoordinates(
  markers: Array<App.SearchMapMarker>,
  offerListBounds?: App.OfferListBounds,
): Array<{ lat: number, lng: number }> {
  const markerCoordinates = markers.map(marker => ({
    lat: marker.latitude,
    lng: marker.longitude,
  }))

  if (!offerListBounds) {
    return markerCoordinates
  }

  return [
    ...markerCoordinates,
    {
      lat: +offerListBounds.southwest.lat,
      lng: +offerListBounds.southwest.lng,
    },
    {
      lat: +offerListBounds.northeast.lat,
      lng: +offerListBounds.northeast.lng,
    },
  ]
}

/*
  Groups offers that either have the same property, or are in the same building, or otherwise are so
  close together that it's not feasible to show separate markers for them.
  To make this algorithm more efficient, first the offers are sorted by their latitude.
  That means for each offer, instead of comparing it against every other offer (which would have
  O(n^2) time complexity), we can stop comparing once we reach an offer that's too far away in
  latitude - each subsequent offer will also be too far away, so we can break that loop and move on.
*/
export function groupOffersAtSameCoordinates(offers: Array<OfferWithLocation>): Array<Array<OfferWithLocation>> {
  const metresThreshold = 10
  const groupedOfferSet = new Set<OfferWithLocation>()
  const groups: Array<Array<OfferWithLocation>> = []

  for (let i = 0; i < offers.length; i++) {
    const referenceOffer = offers[i]

    if (groupedOfferSet.has(referenceOffer)) {
      continue
    }

    const { latitude: lat1, longitude: lon1 } = getGeolocation(referenceOffer)

    const group = [referenceOffer]
    groupedOfferSet.add(referenceOffer)

    // Find offers that are within the threshold distance from `referenceOffer`
    for (let j = i + 1; j < offers.length; j++) {
      const thisOffer = offers[j]
      const { latitude: lat2, longitude: lon2 } = getGeolocation(thisOffer)

      if (groupedOfferSet.has(thisOffer)) {
        continue // Skip offers already in a group
      }

      if (getMetresDistanceFromLatLong(lat1, lon1, lat2, lon2) < metresThreshold) {
        group.push(thisOffer)
        groupedOfferSet.add(thisOffer)
      }
    }

    groups.push(group)
  }

  return groups
}

const vmStorageKey = 'searchMapVisitedMarkers'
export function useVisitedMarkersState() {
  const [visitedMarkerIds, setVisitedMarkerIds] = useState(() => {
    const storedIdsRaw = isomorphicSessionStorage.get(vmStorageKey)
    return new Set<string>(storedIdsRaw ? JSON.parse(storedIdsRaw) : [])
  })

  const addVisitedMarkerId = useCallback((markerId: string) => {
    setVisitedMarkerIds(ids => {
      if (ids.has(markerId)) { return ids }
      const newIds = new Set(ids)
      newIds.add(markerId)
      isomorphicSessionStorage.set(vmStorageKey, JSON.stringify(Array.from(newIds)))
      return newIds
    })
  }, [])

  return [visitedMarkerIds, addVisitedMarkerId] as const
}

export function areOffersBedbank(
  offers: Array<App.Offer | App.BedbankOffer | App.OfferListMetaData>,
): offers is Array<App.BedbankOffer> {
  return offers.some(offer => offer.type === OFFER_TYPE_BED_BANK)
}
